import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { AtiraCheckBoxGroup } from "../../../../components/AtiraCheckBoxGroup";
import { Drawer } from "../../../../components/Drawer";
import { Flex } from "../../../../components/Flex";
import { Info } from "../../../../components/Info";
import { Text } from "../../../../components/Text";
import { integrationSliceSelectors } from "../../../../redux/integration/integration.selector";
import { integrationActions } from "../../../../redux/integration/integration.slice";
import { permissionSliceSelectors } from "../../../../redux/permission/permission.selector";
import { permissionActions } from "../../../../redux/permission/permission.slice";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Spacing } from "../../../../theme/Spacing";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { DrawerDefaultsExtra } from "../../../tasks/components/DrawerDefaultsExtra";

const StyledAtiraCheckBoxGroup = styled(AtiraCheckBoxGroup)`
  flex-direction: column;
  gap: ${Spacing.s};
`;

type Props = {
  open: boolean;
  onClose: () => void;
};

/** Who new WhatsApp chats are assigned to when they are created.
 *
 * Works like a form's auto-assign: picking nobody turns it off. Members only
 * see chats assigned to them, so this is what puts new conversations in front
 * of the team without the owner handing each one out.
 */
export const WAAutoAssignDrawer: React.FC<Props> = ({ open, onClose }) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const integration = useAppSelector(
    integrationSliceSelectors.selectWhatsAppMetaIntegration,
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    dispatch(permissionActions.getMembers({ userId }));

    const autoAssign = integration?.config?.autoAssign;
    setSelected(autoAssign?.enabled ? autoAssign.assigneeIds || [] : []);
  }, [open, dispatch, userId, integration?.config?.autoAssign]);

  const onSave = async () => {
    try {
      setLoading(true);

      await dispatch(
        integrationActions.updateWhatsappMetaAutoAssign({
          userId,
          integrationId: integration!._id,
          enabled: selected.length > 0,
          assigneeIds: selected,
        }),
      ).unwrap();

      await dispatch(integrationActions.getUserIntegrations({ userId }));

      AtiraToast.success(t("integration.whatsapp.meta.autoAssign.success"));
      onClose();
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      title={t("integration.whatsapp.meta.autoAssign.title")}
      open={open}
      onClose={onClose}
      size="large"
      extra={
        <DrawerDefaultsExtra
          onSubmit={onSave}
          onCancel={onClose}
          config={{ loading }}
          submitTitle={t("common.save")}
        />
      }
    >
      <Flex flexDirection="column" gap="m" width="100%">
        <Info info={t("integration.whatsapp.meta.autoAssign.description")} />
        <Info info={t("integration.whatsapp.meta.autoAssign.description2")} />

        {members.length ? (
          <StyledAtiraCheckBoxGroup
            options={members.map((m) => ({ label: m.name, value: m._id }))}
            value={selected}
            onChange={(values) => setSelected(values as string[])}
            fontSize="1rem"
          />
        ) : (
          <Text color="darkerSub">
            {t("integration.whatsapp.meta.assign.noMembers")}
          </Text>
        )}
      </Flex>
    </Drawer>
  );
};
